<script setup lang="ts">
import { computed } from 'vue';
import vTooltip from 'primevue/tooltip';
import { useKycPricing } from '@/composables/useKycPricing';
import { useRiskProfileOptions } from '@/composables/useRiskProfileOptions';
import type { ProfileGroup } from '@/types/risk-profile';

const props = defineProps<{
    group: ProfileGroup;
    store: any; // Shared interface between riskProfileStore and riskProfileTemplateStore
}>();

const { options: riskOptions } = useRiskProfileOptions();
const { calculateExcessEntityCost, freeLimit } = useKycPricing(riskOptions);

const activeInstance = computed(() => props.store.getCurrentInstance(props.group));
const releaseInstance = computed(() => props.store.getLatestRelease(props.group));
const draftInstance = computed(() => props.store.getDraft(props.group));
</script>

<template>
  <div class="flex flex-col gap-1.5 py-1">
      <!-- PRODUCTION PRICE (PRIORITY 1) -->
      <div v-if="activeInstance" 
           class="flex items-center justify-between"
           v-tooltip.top="`Preço da versão ativa em produção: Base + Variável por entidade extra (>${freeLimit})`"
      >
          <div class="flex items-center gap-1 font-mono text-xs text-slate-600">
              <span class="font-bold">{{ activeInstance.base_token_price }}</span>
              <span class="text-[10px] opacity-70">+</span>
              <span class="font-bold">({{ calculateExcessEntityCost(activeInstance) }} * ent.)</span>
          </div>
      </div>

      <!-- LATEST RELEASE PRICE (PRIORITY 2 - Only if no Production) -->
      <div v-else-if="releaseInstance" 
           class="flex items-center justify-between"
           v-tooltip.top="`Preço da última versão lançada (não ativa): Base + Variável por entidade extra (>${freeLimit})`"
      >
          <span class="text-[10px] font-bold uppercase text-slate-400 tracking-wider">v{{ releaseInstance.version_number }}</span>
          <div class="flex items-center gap-1 font-mono text-xs text-slate-600">
              <span class="font-bold">{{ releaseInstance.base_token_price }}</span>
              <span class="text-[10px] opacity-70">+</span>
              <span class="font-bold">({{ calculateExcessEntityCost(releaseInstance) }} * ent.)</span>
          </div>
      </div>

      <!-- DRAFT PRICE (PRIORITY 3 - Only if no Production or Release) -->
      <div v-else-if="draftInstance" 
           class="flex items-center justify-between"
           v-tooltip.top="`Preço do rascunho em andamento: Base + Variável por entidade extra (>${freeLimit})`"
      >
          <div class="flex items-center gap-1 font-mono text-xs text-slate-600">
              <span class="font-bold">{{ draftInstance.base_token_price }}</span>
              <span class="text-[10px] opacity-70">+</span>
              <span class="font-bold">({{ calculateExcessEntityCost(draftInstance) }} * ent.)</span>
          </div>
      </div>
  </div>
</template>

<style scoped>
/* No background or borders as requested for list table styling */
</style>
